import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { SkeletonRows } from 'components/Skeleton'
import { unlinkedToast } from 'features/taskLinks/UndoUnlinkToast'
import taskLinksAPI from 'features/taskLinks/taskLinksAPI'
import LinkBadge from 'features/taskLinks/LinkBadge'
import SuggestionRow from 'features/taskLinks/SuggestionRow'
import { LINKS_EXPLAINER, SUGGESTION_HELP, errorMessage } from 'features/taskLinks/linkCopy'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import { RAG } from 'features/dashboard/charts/scoreColour'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import { BORDER, INK, LINK, MUTED, TextButton } from 'features/actions/workspace/ui'

const SEVERITY_LABEL = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' }

const StatusPill = ({ status }) => {
  const meta = taskStatusMeta(status)
  return <Pill style={{ color: meta.color, background: meta.bg }}>{meta.label}</Pill>
}

const DocumentTaskLinks = ({ bpId, fileId, refreshKey, onChanged }) => {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(null)

  const load = useCallback(() => {
    if (!bpId || !fileId) return
    taskLinksAPI.fileLinks(bpId, fileId)
      .then(res => setData(res?.data?.data || { remedials: [], relatedTasks: [] }))
      .catch(() => setData({ remedials: [], relatedTasks: [], failed: true }))
  }, [bpId, fileId])

  useEffect(() => {
    setData(null)
  }, [bpId, fileId])

  useEffect(() => { load() }, [load, refreshKey])

  const act = async (key, fn, message, relation) => {
    setBusy(key)
    try {
      await fn()
      toast.success(message)
      load()
      onChanged?.(relation)
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  if (!bpId || !fileId) return null

  if (data === null) {
    return (
      <Section aria-busy='true' aria-label='Loading tasks for this document'>
        <SkeletonRows rows={3} gap={10} widths={['100%']} />
      </Section>
    )
  }

  if (data.failed) {
    return (
      <Section>
        <Muted>We could not load the tasks for this document. <Retry type='button' onClick={load}>Try again</Retry></Muted>
      </Section>
    )
  }

  const canManage = !!data.canManage
  const remedials = (data.remedials || []).filter(r => (r.links || []).length || (r.suggestions || []).length)
  const related = data.relatedTasks || []

  if (!remedials.length && !related.length) {
    if (!(data.remedials || []).length) return null
    return (
      <Section aria-labelledby={`doc-tasks-${fileId}`}>
        <Heading id={`doc-tasks-${fileId}`}>Tasks for this document</Heading>
        <Muted>
          No tasks are linked to the remedials from this document yet, and we did not find any existing task that looks like it deals with them.
          Use "Link task" next to a remedial if one already does.
        </Muted>
      </Section>
    )
  }

  return (
    <Section aria-labelledby={`doc-tasks-${fileId}`} data-tour='document-task-links'>
      <Heading id={`doc-tasks-${fileId}`}>Tasks for this document</Heading>
      <Intro>{LINKS_EXPLAINER}</Intro>
      <TasksRemedialsExplainer />

      {remedials.map(rem => {
        const sev = RAG[rem.severity]
        return (
          <Remedial key={rem.id}>
            <RemHead>
              {sev && <Sev style={{ color: sev.fg, background: sev.bg }}>{SEVERITY_LABEL[rem.severity]}</Sev>}
              <RemTitle to={`/actions/${rem.id}`}>{rem.title}</RemTitle>
            </RemHead>
            {(rem.links || []).map(l => (
              <Linked key={l.task.id}>
                <LinkBadge relation={l.relation} source={l.source} />
                <TaskTitle to={`/tasks/${l.task.id}`}>{l.task.title || 'Untitled task'}</TaskTitle>
                <StatusPill status={l.task.status} />
                {canManage && (
                  <Quiet
                    type='button'
                    disabled={busy === `u-${rem.id}-${l.task.id}`}
                    aria-label={`Unlink ${l.task.title || 'task'} from ${rem.title}`}
                    onClick={() => act(`u-${rem.id}-${l.task.id}`, () => taskLinksAPI.unlink(rem.id, l.task.id), unlinkedToast({ text: 'Task unlinked. It will not be suggested again for this remedial.', actionId: rem.id, taskId: l.task.id, relation: l.relation, onRestored: () => { load(); onChanged?.((l.relation || 'handles') === 'handles' ? { handled: rem.id } : null) } }), { unlinked: rem.id })}
                  >
                    Unlink
                  </Quiet>
                )}
              </Linked>
            ))}
            {(rem.suggestions || []).length > 0 && (
              <Suggestions>
                <SubHeading>Possibly already handled by</SubHeading>
                <SubHelp>{SUGGESTION_HELP}</SubHelp>
                {rem.suggestions.map(s => (
                  <SuggestionRow
                    key={s.matchId}
                    title={s.task?.title}
                    to={`/tasks/${s.task?.id}`}
                    statusValue={s.task?.status}
                    deadline={s.task?.deadline}
                    isRoutine={s.task?.isRoutine}
                    reasons={s.reasons}
                    canManage={canManage}
                    busy={busy === s.matchId}
                    onLink={relation => act(s.matchId, () => taskLinksAPI.link(rem.id, s.task.id, relation), 'Task linked to the remedial.', relation === 'handles' ? { handled: rem.id } : null)}
                    onDismiss={() => act(s.matchId, () => taskLinksAPI.dismiss(s.matchId), 'Got it. We will not suggest that task again.')}
                  />
                ))}
              </Suggestions>
            )}
          </Remedial>
        )
      })}

      {related.length > 0 && (
        <Related>
          <SubHeading>Other tasks connected to this document</SubHeading>
          {related.map(r => (
            <RelatedRow key={r.task.id}>
              <Linked>
                <TaskTitle to={`/tasks/${r.task.id}`}>{r.task.title || 'Untitled task'}</TaskTitle>
                <StatusPill status={r.task.status} />
              </Linked>
              <Reason>{(r.reasons || []).join('. ')}</Reason>
            </RelatedRow>
          ))}
        </Related>
      )}
    </Section>
  )
}

export default DocumentTaskLinks

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #fff;
`

const Heading = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
`

const Intro = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`

const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`

const Retry = styled(TextButton)`
  min-height: 0;
  padding: 0;
  font-size: 14px;
`

const Remedial = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid ${BORDER};
`

const RemHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const Sev = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
`

const RemTitle = styled(Link)`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
  text-decoration: none;
  overflow-wrap: anywhere;
  &:hover { text-decoration: underline; }
`

const Linked = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
`

const TaskTitle = styled(Link)`
  font-size: 15px;
  font-weight: 600;
  color: ${LINK};
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
`

const Pill = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const Quiet = styled(TextButton)`
  font-size: 14px;
  color: ${MUTED};
`

const Suggestions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fafafa;
`

const SubHeading = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
`

const SubHelp = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`

const Related = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid ${BORDER};
`

const RelatedRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Reason = styled.span`
  font-size: 14px;
  color: ${MUTED};
`
